/**
 * VideoLibrary component for the "Video Library" section.
 *
 * Handles category filtering, sorting and pagination entirely client-side for the
 * pre-rendered "installation_video" metaobject cards, since Liquid has no generic way
 * to read custom URL query parameters outside of collection/search contexts.
 */
class VideoLibrary extends HTMLElement {
  connectedCallback() {
    this.videosPerPage = parseInt(this.getAttribute('videos-per-page'), 10) || 12;
    this.cards = Array.from(this.querySelectorAll('.video-library-card'));
    this.categoryButtons = Array.from(this.querySelectorAll('.video-library-category-button'));
    this.sortPopover = this.querySelector('x-popover');
    this.paginationContainer = this.querySelector('.video-library-pagination');
    this.countElement = this.querySelector('[data-video-library-count]');
    this.countSingular = this.dataset.countSingular || '';
    this.countPlural = this.dataset.countPlural || '';
    this.page = 1;
    this.category = this.getAttribute('default-category');
    this.sortBy = this.getAttribute('default-sort') || 'newest';

    // The mobile category drawer (<facets-drawer>) and, on mobile, the sort <x-popover>
    // move themselves to a direct child of <body> while open (see theme.js's
    // DialogElement, shouldAppendToBody) to escape stacking/overflow contexts. That
    // takes their contents out of this element's subtree, so listeners attached here
    // would stop receiving their events while open. Listening on the document instead,
    // with explicit ownership checks below, keeps working regardless of where the
    // dialog has been moved to.
    this.boundOnClick = this.onClick.bind(this);
    this.boundOnSortChange = this.onSortChange.bind(this);
    document.addEventListener('click', this.boundOnClick);
    document.addEventListener('listbox:change', this.boundOnSortChange);

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundOnClick);
    document.removeEventListener('listbox:change', this.boundOnSortChange);
  }

  onClick(event) {
    const categoryButton = event.target.closest('.video-library-category-button');
    if (categoryButton && this.categoryButtons.includes(categoryButton)) {
      if (categoryButton.value === this.category) return;

      this.category = categoryButton.value;
      this.page = 1;
      this.categoryButtons.forEach((button) => {
        const isActive = button === categoryButton;
        button.classList.toggle('link-faded', !isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
      this.render();
      return;
    }

    const pageButton = event.target.closest('[data-page]');
    if (pageButton && this.contains(pageButton)) {
      this.page = parseInt(pageButton.dataset.page, 10);
      this.render();
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  onSortChange(event) {
    if (!this.sortPopover || !event.composedPath().includes(this.sortPopover)) return;

    this.sortBy = event.detail.value;
    this.page = 1;
    this.render();
  }

  get filteredCards() {
    const cards = this.category
      ? this.cards.filter((card) => card.dataset.category === this.category)
      : this.cards.slice();

    return cards.sort((a, b) => {
      if (this.sortBy === 'alphabetical') {
        return a.dataset.title.localeCompare(b.dataset.title);
      }
      return new Date(b.dataset.sortDate) - new Date(a.dataset.sortDate);
    });
  }

  render() {
    const filtered = this.filteredCards;
    const totalPages = Math.max(1, Math.ceil(filtered.length / this.videosPerPage));
    this.page = Math.min(this.page, totalPages);

    const start = (this.page - 1) * this.videosPerPage;
    const pageCards = filtered.slice(start, start + this.videosPerPage);
    const visible = new Set(pageCards);

    // Track the real first/last *visible* card in JS (rather than relying on CSS
    // :first-child/:last-child, which only sees static DOM order and doesn't know
    // which cards are currently hidden by category filtering) so the divider CSS
    // can correctly drop the outer-edge border/padding on the actual visible ends.
    this.cards.forEach((card) => {
      card.hidden = !visible.has(card);
      card.classList.toggle('video-library-card--first', card === pageCards[0]);
      card.classList.toggle('video-library-card--last', card === pageCards[pageCards.length - 1]);
    });
    filtered.forEach((card, index) => {
      card.style.order = index;
    });

    this.updateCount(filtered.length);
    this.renderPagination(totalPages);
  }

  updateCount(count) {
    if (!this.countElement) return;

    this.countElement.textContent = count === 1
      ? this.countSingular
      : this.countPlural.replace(/\d+/, count);
  }

  renderPagination(totalPages) {
    if (!this.paginationContainer) return;

    if (totalPages <= 1) {
      this.paginationContainer.innerHTML = '';
      return;
    }

    let html = '';
    for (let page = 1; page <= totalPages; page++) {
      html += `<button type="button" class="pagination__link h6" data-page="${page}" ${page === this.page ? 'aria-current="page"' : ''}>${page}</button>`;
    }
    this.paginationContainer.innerHTML = html;
  }
}

if (!window.customElements.get('video-library')) {
  window.customElements.define('video-library', VideoLibrary);
}
